import React, { useEffect, useState } from 'react';
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import { useNavigate } from 'react-router-dom';
import { API } from 'utilities/api';
import { GlassIconButton } from 'dashboard/components/common';

/**
 * Choosing what goes on a page.
 *
 * Adding a section used to drop a blank "content" block on the page with no
 * choice offered, which made the content library invisible from the one place
 * it matters: a brand who had filled in their team had no way to put the team
 * on a page.
 *
 * Two kinds, deliberately shown side by side:
 *
 *   Blank      — you type into it, and it belongs to this page alone.
 *   Library    — a view of content you filled in once. The page stores which
 *                type to show, not a copy, so editing the team updates every
 *                page showing it and switching this off loses nothing.
 *
 * Empty library types are shown rather than hidden, greyed with the reason.
 * Hiding them would mean a brand never discovers the section exists; showing
 * them as dead ends would be worse — so each one links to the editor that
 * fills it.
 */

/**
 * What each section type is, in the owner's words.
 *
 * The list used to stop at six and was written out by hand here, which is how
 * five types the site can perfectly well draw — pricing, testimonials, team,
 * cards and FAQ — were unreachable: the renderer had layouts for them and
 * nothing ever offered them. The types now come from the server with the
 * section library, and this only supplies the wording.
 */
const TYPE_LABELS = {
  hero: { label: 'Hero', hint: 'Big opening statement with a call to action.' },
  features: { label: 'Features', hint: 'A few short selling points.' },
  cards: { label: 'Cards', hint: 'Steps, services or anything that comes in threes.' },
  content: { label: 'Text', hint: 'A heading and some words.' },
  pricing: { label: 'Pricing', hint: 'What you charge, side by side.' },
  testimonials: { label: 'Testimonials', hint: 'What customers said, and who said it.' },
  team: { label: 'Team', hint: 'The people, with what each one does.' },
  gallery: { label: 'Gallery', hint: 'Images in a grid.' },
  faq: { label: 'Questions', hint: 'The things people ask before buying.' },
  cta: { label: 'Call to action', hint: 'One line and a button.' },
  contact: { label: 'Contact', hint: 'How to reach you.' },
};

const labelFor = (type) => TYPE_LABELS[type]?.label
  || String(type || '').replace(/^./, (ch) => ch.toUpperCase());
const hintFor = (type) => TYPE_LABELS[type]?.hint || '';

// Which section type best carries each kind of library content. Picking this
// for the owner means one fewer decision that has no wrong answer worth
// asking about.
const LIBRARY_SECTION_TYPE = {
  blogs: 'cards', news: 'cards', projects: 'cards', services: 'features',
  ourteam: 'team', ourpartners: 'gallery', ourtrustedclients: 'gallery',
  recommendations: 'testimonials', aboutus: 'content', ourstory: 'content',
  summary: 'content',
};

function Card({ title, hint, chip, disabled, onClick, icon }) {
  return (
    <Box
      role='button'
      tabIndex={disabled ? -1 : 0}
      onClick={() => !disabled && onClick()}
      onKeyDown={(e) => {
        if (disabled) return;
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); }
      }}
      sx={{
        height: '100%', p: '12px', borderRadius: '10px',
        border: '1px solid', borderColor: 'divider',
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.55 : 1,
        transition: 'border-color 120ms ease, background-color 120ms ease',
        '&:hover': disabled ? {} : { borderColor: 'primary.main', bgcolor: 'action.hover' },
        '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 },
      }}
    >
      <Stack direction='row' alignItems='center' justifyContent='space-between' gap='6px'>
        <Stack direction='row' alignItems='center' spacing='6px' minWidth={0}>
          {icon}
          <Typography variant='body2' fontWeight='700' noWrap>{title}</Typography>
        </Stack>
        {chip}
      </Stack>
      <Typography variant='caption' color='text.secondary' display='block' mt='2px'>
        {hint}
      </Typography>
    </Box>
  );
}

export default function AddSectionDialog({ open, onClose, region, brandId, onAdd, onAddPreset }) {
  const navigate = useNavigate();
  const [library, setLibrary] = useState([]);
  const [groups, setGroups] = useState([]);

  useEffect(() => {
    if (!open || !brandId) return;
    API.getLibrarySummary({ brand_id: brandId })
      .then((res) => setLibrary(res?.types || []))
      .catch(() => setLibrary([])); // blank sections still work without it
  }, [open, brandId]);

  // The section library. If it cannot be fetched every type still offers an
  // empty section, so the dialog degrades to what it used to be rather than
  // to nothing.
  useEffect(() => {
    if (!open) return;
    API.listSectionPresets()
      .then((res) => setGroups(Array.isArray(res?.groups) ? res.groups : []))
      .catch(() => setGroups(
        Object.keys(TYPE_LABELS).map((type) => ({ type, presets: [] })),
      ));
  }, [open]);

  // Header and footer are structural; offering a blog list inside a header
  // would be offering a mistake.
  const structural = region === 'header' || region === 'footer';

  const addPreset = (preset) => {
    onAddPreset({ key: preset.key, region });
    onClose();
  };

  const addBlank = (type) => {
    onAdd({ region, type, name: `${type} section`, source: { kind: 'static' } });
    onClose();
  };

  const addLibrary = (t) => {
    onAdd({
      region,
      type: LIBRARY_SECTION_TYPE[t.key] || 'content',
      name: t.label,
      source: {
        kind: 'library',
        libraryType: t.key,
        // Single-entry types have exactly one thing to show; lists default to
        // three, which fills a row without turning the page into an archive.
        limit: t.single ? 1 : 3,
        display: t.single ? 'list' : 'grid',
      },
    });
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth scroll='paper'>
      <DialogTitle sx={{ pr: '48px' }}>
        Add a {region} section
        <GlassIconButton
          label='Close'
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
          size='small'
        >
          <CloseIcon fontSize='small' />
        </GlassIconButton>
      </DialogTitle>

      <DialogContent dividers>
        {!structural && library.length > 0 && (
          <Box mb='24px'>
            <Typography variant='body2' fontWeight='700' mb='2px'>
              From your content library
            </Typography>
            <Typography variant='caption' color='text.secondary' display='block' mb='10px'>
              Shows content you have already filled in. Nothing is copied — edit it once and
              every page using it updates.
            </Typography>
            <Grid container spacing='8px'>
              {library.map((t) => {
                const empty = t.count === 0;
                return (
                  <Grid item xs={12} sm={6} md={4} key={t.key}>
                    <Card
                      title={t.label}
                      icon={<LibraryBooksIcon fontSize='small' color={empty ? 'disabled' : 'primary'} />}
                      hint={empty ? 'Nothing here yet — click to fill it in.' : t.describes}
                      chip={
                        empty
                          ? <Chip size='small' variant='outlined' label='Empty' />
                          : <Chip size='small' color='success' label={t.count} />
                      }
                      disabled={t.available === false}
                      onClick={() => (empty
                        ? navigate(`/dashboard/webpage/${t.key}`)
                        : addLibrary(t))}
                    />
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        )}

        {structural ? (
          <>
            <Typography variant='body2' fontWeight='700' mb='2px'>Blank section</Typography>
            <Typography variant='caption' color='text.secondary' display='block' mb='10px'>
              You type the content, and it belongs to this page only.
            </Typography>
            <Grid container spacing='8px'>
              <Grid item xs={12} sm={6} md={4}>
                <Card
                  title={region === 'header' ? 'Header' : 'Footer'}
                  hint='Just for this page.'
                  onClick={() => addBlank(region)}
                />
              </Grid>
            </Grid>
          </>
        ) : (
          <>
            <Typography variant='body2' fontWeight='700' mb='2px'>Ready-made sections</Typography>
            <Typography variant='caption' color='text.secondary' display='block' mb='14px'>
              Each one arrives written, with real words to edit down rather than an empty box
              to fill. Nothing goes live until you switch it on.
            </Typography>

            {groups.map((g) => (
              <Box key={g.type} mb='18px'>
                <Stack direction='row' alignItems='baseline' gap='8px' mb='6px'>
                  <Typography variant='caption' fontWeight='700' textTransform='uppercase'>
                    {labelFor(g.type)}
                  </Typography>
                  <Typography variant='caption' color='text.secondary'>
                    {hintFor(g.type)}
                  </Typography>
                </Stack>
                <Grid container spacing='8px'>
                  {g.presets.map((pr) => (
                    <Grid item xs={12} sm={6} md={4} key={pr.key}>
                      <Card title={pr.name} hint={pr.description} onClick={() => addPreset(pr)} />
                    </Grid>
                  ))}
                  {/* Always last, and always there: a design is a starting
                      point, not an obligation. */}
                  <Grid item xs={12} sm={6} md={4}>
                    <Card
                      title='Empty'
                      hint={`A blank ${labelFor(g.type).toLowerCase()} you fill in yourself.`}
                      onClick={() => addBlank(g.type)}
                    />
                  </Grid>
                </Grid>
              </Box>
            ))}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
